<template>
    <div class="memory-game">
        <div class="banner-container">
            <img :src="imgUrl($mecanic.scratch_picture, 'scratch')" :alt="'Bannière jeu Memory'"
                class="banner-desktop hidden lg:block w-[70%] mx-auto" />
            <img :src="imgUrl($mecanic.scratch_picture_mobile, 'scratch')" :alt="'Bannière jeu Memory mobile'"
                class="banner-mobile block lg:hidden w-[80%] mx-auto" />
        </div>

        <div v-if="$mecanic.text_scratch" class="w-[95%] md:w-[85%] mx-auto my-6 md:my-8">
            <div class="relative overflow-hidden bg-[#1e3470] border border-white/10 rounded-full p-4 shadow-lg">
                
                <div class="absolute top-[20%] left-[15%] w-12 h-12 md:w-16 md:h-16 bg-yellow-400 rounded-full blur-[20px] md:blur-[30px] opacity-70 pointer-events-none"></div>
                <div class="absolute bottom-[20%] left-[45%] w-12 h-12 md:w-16 md:h-16 bg-yellow-200 rounded-full blur-[20px] md:blur-[30px] opacity-50 pointer-events-none"></div>

                <div class="relative z-10 text-center uppercase text-white font-extrabold text-sm md:text-xl lg:text-3xl tracking-wider leading-snug force-neon-style" v-html="$mecanic.text_scratch">
                </div>
                
            </div>
        </div>

        <div
            class="p-6 my-6 md:my-8 rounded-lg shadow flex flex-col md:flex-row items-center gap-4 bg-black/30 rounded-xl backdrop-blur-sm">
            <div
                class="text-3xl font-bold w-[200px] md:w-[20%] text-[#EDC516] uppercase border-b-4 md:border-r-4 md:border-b-0 border-[#EDC516] pl-4 pb-2 md:pl-4 md:pb-0 italic">
                <p>Comment</p>
                <p>jouer ?</p>
            </div>
            <div class="flex flex-col md:flex-row items-center w-full md:w-[80%] gap-4">
                <div v-if="$mecanic.instructions_1_scratch"
                    class="instruction-item flex items-center w-full md:w-[33%]">
                    <p class="instruction-number">1</p>
                    <p class="!text-white force-white-html font-bold" v-html="$mecanic.instructions_1_scratch"></p>
                </div>
                <div v-if="$mecanic.instructions_2_scratch"
                    class="instruction-item flex items-center w-full md:w-[33%]">
                    <p class="instruction-number">2</p>
                    <p class="!text-white force-white-html font-bold" v-html="$mecanic.instructions_2_scratch"></p>
                </div>
                <div v-if="$mecanic.instructions_3_scratch"
                    class="instruction-item flex items-center w-full md:w-[33%]">
                    <p class="instruction-number">3</p>
                    <p class="!text-white force-white-html font-bold" v-html="$mecanic.instructions_3_scratch"></p>
                </div>
            </div>
        </div>

        <div class="game-container my-4 w-full">
            <div class="grid grid-cols-3 sm:grid-cols-4 gap-3 sm:gap-7 w-fit mx-auto">
                <div v-for="(card, index) in cards" :key="card.uniqueId" 
                        class="card-container cursor-pointer"
                        @click="flipCard(index)">

                    <div class="card-inner" :class="{ 'is-flipped': card.isFlipped || card.isMatched }">    
                        <div class="card-front bg-white border-[4px] md:border-[6px] border-[#eec239] overflow-hidden relative ">
                            <div class="absolute inset-0 bg-[radial-gradient(#1e3a8a_2px,transparent_2px)] [background-size:16px_16px] opacity-20"></div>                
                            <div class="absolute inset-[6px] md:inset-[8px] border border-[#1e3a8a]/20 rounded-xl pointer-events-none"></div>                            
                            <img alt="Logo" class="relative z-10 w-[75%] h-auto opacity-50 select-none object-contain" referrerpolicy="no-referrer" :src="imgUrl($template.logo, 'campaign_logo')">                            
                        </div>
                        
                        <div class="card-back bg-white border-[4px] md:border-[6px] border-[#cf951d] overflow-hidden relative">
                            <img :src="imgUrl(card.image, 'memory')" alt="Face" class="w-full h-full object-cover">
                            <transition name="fade">
                                <div v-if="card.isMatched" class="absolute inset-0 z-10 flex items-center justify-center bg-[#eec239]/20">
                                    <div class="bg-white rounded-full w-[50px] h-[50px] flex items-center justify-center shadow-lg">
                                        <svg class="w-8 h-8 text-[#26428B]" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="4" d="M5 13l4 4L19 7"></path>
                                        </svg>
                                    </div>
                                </div>
                            </transition>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>

    <div v-if="gameFinished" ref="endGameSection" class="w-full flex flex-col justify-center mt-4"
        style="scroll-margin-top: 500px;">
        <div class="text-center">
            <p class="mb-4 text-2xl" v-html="$mecanic.end_text_scratch"></p>
            <p class="m-auto w-fit sm:mt-4 font-bold text-[14px] lg:text-[24px] text-black">Voici votre numéro de participation</p>
            <CodeTable v-if="code" :code="code" class="mt-4" />
        </div>
    </div>
</template>

<script>
import CodeTable from './CodeTable.vue'

export default {
    name: 'MemoryGame',
    emits: ['endGame'],
    components: {
        CodeTable
    },
    props: {
        code: String
    },
    data() {
        return {
            gameFinished: false,
            cards: [], // Tableau des cartes du jeu
            flippedCards: [], // Cartes actuellement retournées
            matchedPairs: 0, // Nombre de paires trouvées
            isProcessing: false // Indique si une vérification de paire est en cours pour éviter les clics rapides
        }
    },
    mounted() {
        this.initGame();
    },
    methods: {
        initGame() {
            // Récupérer les images de cartes depuis le template et créer les paires
            const baseImages = [
                this.$mecanic.card_1,
                this.$mecanic.card_2,
                this.$mecanic.card_3,
                this.$mecanic.card_4,
                this.$mecanic.card_5,
                this.$mecanic.card_6
            ].filter(img => img !== null && img !== undefined);

            let gameCards = [];
            // Créer deux cartes pour chaque image et les ajouter au tableau du jeu
            baseImages.forEach((image, index) => {
                gameCards.push({
                    id: index,
                    image: image,
                    isFlipped: false,
                    isMatched: false,
                    uniqueId: `card_${index}_a`
                });
                gameCards.push({
                    id: index,
                    image: image,
                    isFlipped: false,
                    isMatched: false,
                    uniqueId: `card_${index}_b`
                });
            });

            // Mélanger les cartes de manière aléatoire
            gameCards.sort(() => Math.random() - 0.5);

            this.cards = gameCards;
        },

        flipCard(index) {
            // Empêcher de retourner plus de deux cartes ou de cliquer pendant la vérification
            if (this.isProcessing || this.flippedCards.length >= 2) return;
            
            const card = this.cards[index];
            
            // Empêcher de retourner une carte déjà retournée ou déjà appariée
            if (card.isFlipped || card.isMatched) return;

            card.isFlipped = true;
            this.flippedCards.push({ card, index });

            if (this.flippedCards.length === 2) {
                // Vérifier si les deux cartes retournées forment une paire
                this.checkMatch();
            }
        },

        checkMatch() {
            this.isProcessing = true;
            // Récupérer les deux cartes retournées
            const [first, second] = this.flippedCards;

            // Si les cartes ont le même ID, elles forment une paire
            if (first.card.id === second.card.id) {
                setTimeout(() => {
                    this.cards[first.index].isMatched = true;
                    this.cards[second.index].isMatched = true;
                    this.matchedPairs++;
                    this.flippedCards = [];
                    this.isProcessing = false;
                    
                    this.checkWinCondition();
                }, 400); 
            } else {
                // Si les cartes ne correspondent pas, les retourner à nouveau après une courte pause
                setTimeout(() => {
                    this.cards[first.index].isFlipped = false;
                    this.cards[second.index].isFlipped = false;
                    this.flippedCards = [];
                    this.isProcessing = false;
                }, 1000);
            }
        },

        checkWinCondition() {
            // Vérifier si toutes les paires ont été trouvées
            if (this.matchedPairs === (this.cards.length / 2)) {
                this.gameFinished = true;
                this.$emit('endGame', true);

                this.$nextTick(() => {
                    setTimeout(() => {
                        this.$refs.endGameSection?.scrollIntoView({
                            behavior: 'smooth',
                            block: 'start'
                        });
                    }, 500);
                });
            }
        }
    }
}
</script>

<style scoped>
.memory-game {
    background-image: v-bind("$mecanic?.background_scratch ? `url(${imgUrl($mecanic.background_scratch, 'scratch')})` : 'none'");
    background-position: center;
    min-height: 100vh;
    @apply p-4 md:p-6 bg-auto md:bg-cover;
}

@media (max-width: 1024px) {
    .memory-game {
        background-image: v-bind("$mecanic?.background_scratch_mobile ? `url(${imgUrl($mecanic.background_scratch_mobile, 'scratch')})` : ($mecanic?.background_scratch ? `url(${imgUrl($mecanic.background_scratch, 'scratch')})` : 'none')");
    }
}

.force-white-html :deep(*) {
    color: #fff !important;
}

.instruction-number {
    @apply bg-[#EDC516] text-black rounded-full w-[40px] h-[40px] flex items-center justify-center text-lg font-bold mr-3 flex-shrink-0 border-2 border-white;
}

.card-container {
    width: 100px;
    height: 140px;
    perspective: 1000px;
    @apply sm:w-[120px] sm:h-[160px] lg:w-[220px] lg:h-[290px];
}

.card-inner {
    width: 100%;
    height: 100%;
    position: relative;
    transition: transform 0.6s cubic-bezier(0.4, 0.2, 0.2, 1);
    transform-style: preserve-3d;
}

.card-inner.is-flipped {
    transform: rotateY(180deg);
}

.card-front, .card-back {
    width: 100%;
    height: 100%;
    position: absolute;
    backface-visibility: hidden;
    border-radius: 1.3rem;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.3);
    overflow: hidden; 
}

.card-front {
    display: flex;
    align-items: center;
    justify-content: center;
}

.card-back {
    transform: rotateY(180deg);
    display: flex;
    align-items: center;
    justify-content: center;
}

.fade-enter-active, .fade-leave-active {
    transition: opacity 0.4s ease;
}
.fade-enter-from, .fade-leave-to {
    opacity: 0;
}
</style>